import { logger } from "@jslib/shared/utils/logger";

import { getInstallPageUrl, getInstallType, getPublishedVersion } from "./ApiUrls";

/**
 * Client identification the backend uses to attribute a verification to a
 * client, version, and install context. HTTP header names are
 * case-insensitive, query string params are not, so both carriers below use
 * these names exactly as spelled here.
 */
const getSharedClientParams = (): Record<string, string> => ({
  clientName: "jslib",
  clientVersion: getPublishedVersion(),
  installType: getInstallType(),
  projectSha: window.sheerIdProjectSha || "",
});

/**
 * The client params sent as headers on API calls.
 */
export const getClientParams = (): Record<string, string> => ({
  ...getSharedClientParams(),
  installUrl: getInstallPageUrl() || "",
});

/**
 * The client params a navigation to the SheerID platform can carry. A
 * navigation has no headers of ours, so these stand in for the client headers
 * plus the Origin and Referer a POST from the form page would have sent. The
 * url is browser-visible (history, address bar, access logs), so the install
 * page url is omitted and the referer keeps only the form page's origin and
 * path: the customer's full page url and its query string are where email
 * addresses and order tokens appear.
 */
const getClientNavigationParams = (): Record<string, string> => {
  const { origin, pathname } = window.location;

  return {
    ...getSharedClientParams(),
    origin,
    referer: `${origin}${pathname}`,
  };
};

/**
 * Carry the client params on a navigation to the SheerID platform, which has no
 * request of its own to put headers on.
 */
export const appendClientParams = (url: string): string => {
  try {
    const destination = new URL(url);

    Object.entries(getClientNavigationParams())
      .filter(([, value]) => value)
      .forEach(([name, value]) => destination.searchParams.set(name, value));

    return destination.toString();
  } catch (e) {
    // Reporting the client is never worth losing the navigation over.
    logger.error(e, "appendClientParams");
    return url;
  }
};
